import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MvsCoreService} from '../../../../core/shared/service/mvs-core.service';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {ObjectRequestList} from '../../../../core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from '../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../core/shared/misc/sorting';
import {
    BdDunningTypeChargeDto,
    BdDunningTypeExitDto,
    BdDunningTypeLevelActionDto,
    BdDunningTypeLevelDto,
    BdDunningTypeLevelSpecialHandlingDto,
    BdDunningTypeLevelTransitionDto,
    BdDunningTypeStartDto,
    BdDunningTypeStopDto
} from '../../model/dto/api/bd-dunning-type-structure.dto';

/**
 * Loads the STRUCTURAL DATA of the DunningType configuration overview (start
 * conditions, dunning levels, transitions, actions, charges, stop conditions,
 * special handlings, exit points) generically via {@code ObjectRequestList},
 * filtered on {@code dunningType} — the same pattern
 * {@code BdDunningTypeChildTableComponent} already uses for the child tables of
 * the object component (requirement §6.4).
 *
 * Deliberately NO {@code QlQueryBuilder}: these seven (+ one exception) child
 * entities are already read generically through the object endpoint today; a
 * new, parallel QL read path would just be an unnecessary second access route
 * for the same data. The one derivation that QL/ObjectRequestList fundamentally
 * cannot deliver — the resolved Modell-C offsets incl. provenance and the
 * example date chain — comes separately via
 * {@link DunningTypeService.getDeadlineDerivation} (requirement §11.1).
 *
 * Read-only: no entity DTOs, no persistence paths. The generic list responses
 * are read as read-shape records (requirement §1.8).
 *
 * @ticket bd-dunningtype-overview
 */
@Injectable({providedIn: 'root'})
export class BdDunningTypeStructureService {

    constructor(private readonly coreService: MvsCoreService) {
    }

    /**
     * Loads the dunning levels of a DunningType, sorted by {@code shortName}
     * (position-stable key M1…M4, see Finding 1.6 / business rule 4). NOT by
     * id, NOT by creation date.
     */
    getLevels(dunningTypeId: number): Observable<BdDunningTypeLevelDto[]> {
        return this.listByDunningType<BdDunningTypeLevelDto>(
            'bd.DunningTypeLevel', dunningTypeId, [new Sorting('shortName', true)]);
    }

    /** Loads the start conditions (paths A/B/C) of a DunningType, sorted by {@code orderPriority}. */
    getStarts(dunningTypeId: number): Observable<BdDunningTypeStartDto[]> {
        return this.listByDunningType<BdDunningTypeStartDto>(
            'bd.DunningTypeStart', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /** Loads the level transitions of a DunningType. */
    getLevelTransitions(dunningTypeId: number): Observable<BdDunningTypeLevelTransitionDto[]> {
        return this.listByDunningType<BdDunningTypeLevelTransitionDto>(
            'bd.DunningTypeLevelTransition', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /** Loads the actions per level of a DunningType, sorted by {@code orderPriority}. */
    getLevelActions(dunningTypeId: number): Observable<BdDunningTypeLevelActionDto[]> {
        return this.listByDunningType<BdDunningTypeLevelActionDto>(
            'bd.DunningTypeLevelAction', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /** Loads the charges of a DunningType, sorted by {@code orderPriority}. */
    getCharges(dunningTypeId: number): Observable<BdDunningTypeChargeDto[]> {
        return this.listByDunningType<BdDunningTypeChargeDto>(
            'bd.DunningTypeCharge', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /** Loads the stop conditions of a DunningType, sorted by {@code orderPriority}. */
    getStops(dunningTypeId: number): Observable<BdDunningTypeStopDto[]> {
        return this.listByDunningType<BdDunningTypeStopDto>(
            'bd.DunningTypeStop', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /** Loads the exit points of a DunningType, sorted by {@code orderPriority}. */
    getExits(dunningTypeId: number): Observable<BdDunningTypeExitDto[]> {
        return this.listByDunningType<BdDunningTypeExitDto>(
            'bd.DunningTypeExit', dunningTypeId, [new Sorting('orderPriority', true)]);
    }

    /**
     * Loads the special-handling rules for a set of dunning levels.
     *
     * EXCEPTION (requirement §6.4): {@code bd.DunningTypeLevelSpecialHandling}
     * has NO {@code dunningType} FK, it only hangs off {@code dunningTypeLevel}.
     * The caller therefore loads the levels via {@link getLevels} first and
     * passes the resulting level ids here; filtering uses {@code cOperatorIn}
     * on {@code dunningTypeLevel}.
     *
     * @param dunningTypeLevelIds previously loaded ids of the dunning levels
     *                            (from {@link getLevels}); an empty list
     *                            returns {@code []} with no HTTP call
     */
    getLevelSpecialHandlings(dunningTypeLevelIds: number[]): Observable<BdDunningTypeLevelSpecialHandlingDto[]> {
        if (!dunningTypeLevelIds?.length) {
            return of([]);
        }

        const objectType = 'bd.DunningTypeLevelSpecialHandling';
        const crudService = this.coreService.getCrudService<MvsCrudService>(objectType);
        if (!crudService) {
            console.error(`bd-dunningtype-overview: MvsCrudService not resolvable for ${objectType}`);
            return of([]);
        }

        const request = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('dunningTypeLevel', FilterCriteria.cOperatorIn, dunningTypeLevelIds)],
            [new Sorting('orderPriority', true)]
        );

        return crudService.list(request).pipe(
            map(result => (result.entries ?? []) as unknown as BdDunningTypeLevelSpecialHandlingDto[]),
            catchError(err => {
                console.error(`bd-dunningtype-overview: list failed for ${objectType}`, err);
                return of([]);
            })
        );
    }

    /**
     * Generic load helper: resolves the crud service for {@code objectType}
     * via {@link MvsCoreService} (dynamic entity resolution, SR1: null check
     * mandatory) and filters on {@code dunningType = dunningTypeId} — the
     * pattern from {@code BdDunningTypeChildTableComponent}.
     */
    private listByDunningType<T>(objectType: string, dunningTypeId: number, sorting: Sorting[]): Observable<T[]> {
        const crudService = this.coreService.getCrudService<MvsCrudService>(objectType);
        if (!crudService) {
            console.error(`bd-dunningtype-overview: MvsCrudService not resolvable for ${objectType}`);
            return of([]);
        }

        const request = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('dunningType', FilterCriteria.cOperatorEqual, dunningTypeId)],
            sorting
        );

        return crudService.list(request).pipe(
            map(result => (result.entries ?? []) as unknown as T[]),
            catchError(err => {
                console.error(`bd-dunningtype-overview: list failed for ${objectType}`, err);
                return of([]);
            })
        );
    }
}
